import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { FREE_STANDARD_THRESHOLD, FREE_TWO_DAY_THRESHOLD } from '../../components/FreeShippingProgress';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Custom hook: encapsulates shipping option fetch, default selection, and
 * the "auto-promote to free 2-day when cart qualifies" rule that was
 * previously inlined in CheckoutPage. Returns the current option list +
 * selection state + a memoised effective-rate calculator.
 */
export function useCheckoutShipping(cart, promoResult) {
  const [shippingOptions, setShippingOptions] = useState([]);
  const [selectedShipping, setSelectedShipping] = useState(null);

  // 1. Fetch the three flat-rate shipping options once.
  useEffect(() => {
    axios.get(`${API}/shipping/options`).then(r => {
      const opts = r.data.options || [];
      setShippingOptions(opts);
      setSelectedShipping(prev => prev || opts[0] || null);
    }).catch((e) => {
      // eslint-disable-next-line no-console
      console.warn('shipping/options fetch failed:', e?.message);
    });
  }, []);

  // 2. When 2-Day becomes free (cart >= threshold) auto-promote so the user
  //    sees the better option pre-selected. Re-runs whenever the discount or
  //    cart subtotal changes.
  const subtotalAfterDiscount = Math.max(
    0,
    (cart?.total || 0) - (promoResult?.valid ? (cart?.total || 0) * promoResult.discount_percent / 100 : 0)
  );
  useEffect(() => {
    if (!shippingOptions.length) return;
    const twoDayFree = subtotalAfterDiscount >= FREE_TWO_DAY_THRESHOLD;
    if (twoDayFree && (!selectedShipping || selectedShipping.service_code === 'standard')) {
      const twoDay = shippingOptions.find(o => o.service_code === 'two_day');
      if (twoDay) setSelectedShipping(twoDay);
    }
  }, [subtotalAfterDiscount, shippingOptions, selectedShipping]);

  // Mirror the backend's free-shipping rules so the UI never shows a different
  // shipping cost than what gets charged.
  const effectiveOptionRate = useCallback((opt) => {
    if (!opt) return 0;
    if (opt.service_code === 'standard' && subtotalAfterDiscount >= FREE_STANDARD_THRESHOLD) return 0;
    if (opt.service_code === 'two_day' && subtotalAfterDiscount >= FREE_TWO_DAY_THRESHOLD) return 0;
    return opt.rate;
  }, [subtotalAfterDiscount]);

  return { shippingOptions, selectedShipping, setSelectedShipping, effectiveOptionRate };
}

/**
 * Returns true when the cart contains ONLY the gratuity "Tip" product (and
 * no physical SKUs). Tip is digital — no shipping options, no shipping cost,
 * no shipping section on the checkout page.
 */
export function isTipOnlyCart(cart) {
  const items = cart?.items || [];
  if (items.length === 0) return false;
  return items.every((i) => i.product_id === 'tip');
}


/**
 * Custom hook: derived cart totals (discount, shipping, final). Pure
 * computation against the inputs — no state, just memoised math so JSX
 * doesn't recompute every render.
 */
export function useCheckoutTotals(cart, promoResult, selectedShipping, effectiveOptionRate) {
  return useMemo(() => {
    const discountAmount = promoResult?.valid
      ? (cart.total * promoResult.discount_percent / 100)
      : 0;
    const discountedSubtotal = Math.max(0, cart.total - discountAmount);
    const tipOnly = isTipOnlyCart(cart);
    // Tip-only carts skip the shipping line entirely — no physical fulfillment.
    const shippingCost = (tipOnly || discountedSubtotal <= 0)
      ? 0
      : effectiveOptionRate(selectedShipping);
    const isShipFree = shippingCost === 0;
    const baseLabel = selectedShipping
      ? `${selectedShipping.service_name} (${selectedShipping.transit_time})`
      : 'Standard Shipping';
    const shippingLabel = tipOnly
      ? null
      : discountedSubtotal <= 0
        ? 'Free Shipping'
        : (isShipFree ? `Free ${baseLabel}` : baseLabel);
    return {
      discountAmount,
      discountedSubtotal,
      shippingCost,
      shippingIsFree: isShipFree,
      shippingLabel,
      tipOnly,
      finalTotal: Math.max(0, discountedSubtotal + shippingCost),
    };
  }, [cart, promoResult, selectedShipping, effectiveOptionRate]);
}
